import dayjs from 'dayjs'
import { uid } from 'radash'
import { useMemo, useState } from 'react'
import AvatarPet from '~components/ui/avatar/avatar-pet'
import Button from '~components/ui/button'
import Icon from '~components/ui/icon'
import Spinner from '~components/ui/loading-indicator/spinner'
import TextInputLabel from '~components/ui/text-input/@base/label'
import { default as Pet } from '~models/pet'
import { S3 } from '~models/s3'
import useFileUpload from '~repositories/hooks/use-file-upload'
import useUpdatePet from '~repositories/hooks/use-update-pet'
import PetAge from './components/pet-age'
import PetGender from './components/pet-gender'
import PetName from './components/pet-name'
import PetType from './components/pet-type'
import usePetInfo from './hooks/use-pet-info'

interface Props {
  pet: Pet.ILevel1
  onSuccess: (data: Pet.ILevel1, variables: Pet.ILevel1) => void
  onMutate?: (req: Pet.ILevel1) => void
  onSettled?: () => void
}

const EditPetInfo = ({ pet, onMutate, onSuccess, onSettled }: Props) => {
  const id = uid(10)
  const {
    nameErrorMessage,
    formattedAgeDate,
    isAgeValidating,
    ageErrorMessage,
    pet: draftPet,
    setPet,
  } = usePetInfo(pet)
  const [isMutating, setIsMutating] = useState(false)
  const { name, gender, species, breed, is_neutered, photo_url } = draftPet
  const updatePetInfo = useUpdatePet({
    onMutate: (req: Pet.Level1) => {
      onMutate?.(req)
      setIsMutating(true)
    },
    onSuccess,
    onSettled: () => {
      onSettled?.()
      setIsMutating(false)
    },
  })

  const { isUploading, ...imageUploadProps } = useFileUpload({
    type: 'image',
    maxNum: 1,
    currentFileNum: 0,
    resourceType: S3.ResourceType.PET_PROFILE,
    setPreviews: response => {
      if (response.length > 0) {
        setPet({ type: 'SET_PHOTO_URL', payload: response[0] })
      }
    },
    onChange: response => {
      setPet({ type: 'SET_PHOTO_URL', payload: response[0].remoteUri })
    },
  })

  const updatePet = () => {
    updatePetInfo({
      ...pet,
      name,
      gender,
      kind: { depth1: species, depth2: breed },
      born_at: new Date(formattedAgeDate).toISOString(),
      born_year: new Date(formattedAgeDate).getFullYear(),
      is_neutered,
      photo_url: photo_url ?? '',
    })
  }

  const onConfirm = () => {
    updatePet()
  }

  const hasChanges = useMemo(
    () =>
      Object.values({
        petName: name !== pet.name,
        petType: species !== pet.kind.depth1 || breed !== pet.kind.depth2,
        gender: gender !== pet.gender || is_neutered !== pet.is_neutered,
        age: pet?.born_at && formattedAgeDate !== dayjs(pet.born_at).format('MM/DD/YYYY'),
        photo_url: photo_url !== pet.photo_url,
      }).some(Boolean),
    [name, species, breed, gender, is_neutered, formattedAgeDate, photo_url, pet]
  )

  const isSaveEnabled = useMemo(
    () =>
      Object.values({
        petName: !!name?.length && !nameErrorMessage?.length,
        petType: !!species && !!breed,
        gender: gender !== null && is_neutered !== null,
        age: !!formattedAgeDate && !ageErrorMessage?.length && !isAgeValidating,
        hasChanges,
        isMutating: !isMutating,
      }).every(Boolean),
    [
      name,
      nameErrorMessage,
      species,
      breed,
      gender,
      is_neutered,
      formattedAgeDate,
      ageErrorMessage,
      isAgeValidating,
      hasChanges,
      isMutating,
    ]
  )

  return (
    <main>
      <header className="flex items-center justify-center w-full h-[56px]">
        <h1 className="text-title-2 text-center text-gray-900 dark:text-white">Edit Pet Info</h1>
      </header>
      <section className="flex items-center justify-center w-full py-5">
        <button type="button" className="relative bg-transparent border-none cursor-pointer">
          {isUploading ? (
            <div className="w-[96px] h-[96px] flex items-center justify-center border-[2px] border-white rounded-full bg-white">
              <Spinner />
            </div>
          ) : (
            <AvatarPet pet={draftPet} size="96" className="border-white border-[2px]" />
          )}
          <div className="absolute bottom-[-4px] right-[-4px] bg-gray-600 w-8 h-8 rounded-full flex items-center justify-center border-white border-[2px]">
            <Icon name="camera_sm" className="size-4 fill-white" />
          </div>
          <label
            aria-label="hidden-element"
            htmlFor={id}
            className="absolute inset-0 z-10 cursor-pointer aria-disabled:cursor-default"
          >
            <input
              {...imageUploadProps}
              type="file"
              name="file"
              id={id}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </label>
        </button>
      </section>
      <div className="h-full overflow-y-scroll no-scrollbar">
        <PetName
          label="Name"
          value={name || ''}
          errorMessage={nameErrorMessage}
          onNameChange={name => {
            setPet({ type: 'SET_NAME', payload: name })
          }}
          className="mt-4"
        />
        <div className="mt-4">
          <TextInputLabel>Species</TextInputLabel>
          <PetType
            species={species}
            breed={breed}
            onBreedSelect={breed => {
              setPet({ type: 'SET_BREED', payload: breed })
            }}
            onSpeciesSelect={species => {
              setPet({ type: 'SET_SPECIES', payload: species })
            }}
          />
        </div>
        <div className="mt-4">
          <TextInputLabel>Gender</TextInputLabel>
          <PetGender
            gender={gender}
            isNeutered={is_neutered}
            onGenderSelect={gender => {
              setPet({ type: 'SET_GENDER', payload: gender })
            }}
            onNeuteredStatusSelect={status => {
              setPet({ type: 'SET_IS_NEUTERED', payload: status })
            }}
          />
        </div>
        <div className="mt-4">
          <TextInputLabel>Birthday</TextInputLabel>
          <PetAge
            onAgeChange={age => {
              setPet({ type: 'SET_AGE', payload: age })
            }}
            age={formattedAgeDate}
            ageErrorMessage={ageErrorMessage}
          />
        </div>
        <Button
          type="button"
          priority="primary"
          className="mt-[48px] w-full"
          onClick={onConfirm}
          disabled={!isSaveEnabled}
        >
          {isMutating ? <Spinner /> : 'Save'}
        </Button>
      </div>
    </main>
  )
}

export default EditPetInfo
